import { browser } from "$app/environment";
import type { RouteId } from "$app/types";

import { derived, type Readable, readable, writable } from "svelte/store";

import { isRouteUnderAny } from "$lib/core/route-id";

/** The user's theme preference. "system" follows the OS color scheme. */
export const THEME_PREFERENCES = ["dark", "light", "system"] as const;
export type ThemePreference = (typeof THEME_PREFERENCES)[number];

/** The concrete theme applied to the document. */
export type ResolvedTheme = "dark" | "light";

/**
 * The preference is stored in a cookie (rather than localStorage) so the
 * server can render `data-theme` on `<html>` during SSR — see `handleTheme`
 * in `hooks.server.ts` — avoiding a flash of the default theme before
 * hydration.
 */
export const THEME_COOKIE_NAME = "modal-theme";

const THEME_COOKIE_MAX_AGE_SECONDS = 400 * 24 * 60 * 60; // browser cap

/**
 * Surfaces pinned to one theme regardless of the preference, by route-id
 * prefix. Every other route follows the preference, so a new surface is themed
 * by default and a surface that is not ready looks visibly wrong in light mode
 * rather than silently opting out.
 *
 * Delete an entry once the surface's colors are migrated to semantic tokens.
 * `/(marketing)` is permanent — the marketing site is deliberately dark and is
 * outside the migration's scope (see `eslint-rules/no-legacy-colors.js`).
 */
export const PINNED_DARK_LAYOUTS = [
  "/(marketing)",
  "/(docs)",
  "/(auth)/(signin)",
  "/(auth)/oauth",
  "/(microsites)",
] as const satisfies readonly RouteId[];

/**
 * The theme a surface is pinned to, or `null` if it follows the preference.
 *
 * Route ids keep their layout-group prefix even for a `+page@.svelte` that
 * resets out to the root layout, so prefix matching classifies those correctly
 * without needing to know which layout mounts.
 */
export function pinnedTheme(routeId: string | null): ResolvedTheme | null {
  // A null route id means no route matched (e.g. a 404), which renders outside
  // any themed surface.
  if (routeId === null) return "dark";
  return isRouteUnderAny(routeId, PINNED_DARK_LAYOUTS) ? "dark" : null;
}

/** The theme to render: the surface's pin if it has one, else the preference. */
export function effectiveTheme(
  routeId: string | null,
  resolved: ResolvedTheme,
): ResolvedTheme {
  return pinnedTheme(routeId) ?? resolved;
}

/** The preference applied when the user has not made a choice. */
export const DEFAULT_PREFERENCE: ThemePreference = "dark";

export function isThemePreference(value: unknown): value is ThemePreference {
  return THEME_PREFERENCES.includes(value as ThemePreference);
}

function readPreferenceCookie(): ThemePreference {
  if (!browser) return DEFAULT_PREFERENCE;
  const value = document.cookie
    .split("; ")
    .find((cookie) => cookie.startsWith(`${THEME_COOKIE_NAME}=`))
    ?.split("=")[1];
  return isThemePreference(value) ? value : DEFAULT_PREFERENCE;
}

function writePreferenceCookie(value: ThemePreference) {
  if (!browser) return;
  const secure = location.protocol === "https:" ? "; secure" : "";
  document.cookie = `${THEME_COOKIE_NAME}=${value}; path=/; max-age=${THEME_COOKIE_MAX_AGE_SECONDS}; samesite=lax${secure}`;
}

const storedPreference = writable<ThemePreference>(readPreferenceCookie());

/** A readable theme preference that can also be set (but not `update`d). */
export type ThemePreferenceStore = Readable<ThemePreference> & {
  set: (value: ThemePreference) => void;
};

/** The persisted theme preference, falling back to `DEFAULT_PREFERENCE`. */
export const themePreference: ThemePreferenceStore = {
  subscribe: storedPreference.subscribe,
  set: (value: ThemePreference) => {
    writePreferenceCookie(value);
    storedPreference.set(value);
  },
};

/** The OS color scheme, tracked live via `prefers-color-scheme`. */
const systemTheme = readable<ResolvedTheme>("dark", (set) => {
  if (!browser || typeof window.matchMedia !== "function") return;
  const query = window.matchMedia("(prefers-color-scheme: light)");
  const update = () => set(query.matches ? "light" : "dark");
  update();
  query.addEventListener("change", update);
  return () => query.removeEventListener("change", update);
});

/** The concrete theme to render: the preference, with "system" resolved. */
export const resolvedTheme: Readable<ResolvedTheme> = derived(
  [themePreference, systemTheme],
  ([preference, system]) => (preference === "system" ? system : preference),
);
